// WHAT: Session history tab component for displaying MiracleFlow daily session summaries
// WHY:  Provides overview of completed sessions with statistics and daily breakdown
// DOMAIN: Productivity → MiracleFlow → Session History
// DEPENDENCIES: useDailySessionSummaries hook, DailySessionCard component
// RISKS:
//   - NOTE: Statistics are calculated client-side and may not match backend totals
//   - WARN: Completion rate calculation may be inaccurate with partial data
//   - NOTE: Loading states use skeleton placeholders, not actual data structure

import type { JSX } from "react";

import {
  ArrowLeft,
  RefreshCw,
  Calendar,
  TrendingUp,
  Clock,
  AlertTriangle,
  History,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { Card, Button, Stack, Heading, Badge } from "@/components/ui";
import { cn } from "@/shared/utils";

import { DailySessionCard } from "../components/molecules/DailySessionCard";
import { useDailySessionSummaries } from "../hooks/useDailySessionSummaries";

/**
 * WHAT:
 *   React component for displaying MiracleFlow session history with statistics and daily summaries.
 *
 * WHY:
 *   Provides users with overview of their productivity session history, completion rates,
 *   and daily session details for tracking progress over time.
 *
 * BEHAVIOR:
 *   - Loads daily session summaries on mount
 *   - Displays statistics (total days, completion rate, total sessions)
 *   - Renders daily session cards in grid layout
 *   - Supports manual refresh of data
 *   - Shows empty state when no sessions exist
 *   - Handles loading and error states
 *
 * RISKS:
 *   - WARN: Statistics calculated client-side may not match backend totals
 *   - NOTE: Completion rate uses filter on completed_sessions > 0, may miss partial completions
 *   - WARN: Loading skeleton shows 6 cards, actual data may have different count
 *
 * @returns {JSX.Element} The session history tab component
 *
 */
export const MiracleFlowSessionHistoryTab: React.FC = () => {
  const navigate = useNavigate();
  const {
    data: dailySummaries,
    error: summariesError,
    isLoading: summariesLoading,
    refetch: refetchSummaries,
  } = useDailySessionSummaries();

  // Helper function to handle navigation
  const handleNavigateToDashboard = (): void => {
    const result = navigate("/miracle-flow");
    if (result instanceof Promise) {
      result.catch(() => {
        // Navigation errors are handled silently
      });
    }
  };

  // Helper function to handle refresh
  const handleRefresh = (): void => {
    refetchSummaries().catch(() => {
      // Refetch errors are handled silently
    });
  };

  // Helper function to calculate completion rate
  const calculateCompletionRate = (summaries: typeof dailySummaries): number => {
    if (!summaries || summaries.length === 0) {
      return 0;
    }

    const completedCount = summaries.filter(
      (s) =>
        (s.morning_session === undefined ? 0 : 1) + (s.evening_session === undefined ? 0 : 1) > 0,
    ).length;

    return Math.round((completedCount / summaries.length) * 100);
  };

  // Helper function to calculate total sessions
  const calculateTotalSessions = (summaries: typeof dailySummaries): number => {
    if (!summaries) {
      return 0;
    }

    let total = 0;
    for (const summary of summaries) {
      const morningCount = summary.morning_session === undefined ? 0 : 1;
      const eveningCount = summary.evening_session === undefined ? 0 : 1;
      total = total + morningCount + eveningCount;
    }

    return total;
  };

  // Helper function to get session history content
  const getSessionHistoryContent = (): JSX.Element => {
    if (summariesLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }, (_, index) => (
            <Card
              className="bg-[#0F172A] border-[#334155] animate-pulse"
              key={`skeleton-${String(index)}`}
              padding="md"
              variant="default"
            >
              <div className="h-4 bg-[#334155] rounded mb-2" />
              <div className="h-8 bg-[#334155] rounded mb-2" />
              <div className="h-3 bg-[#334155] rounded" />
            </Card>
          ))}
        </div>
      );
    }

    if (dailySummaries && dailySummaries.length > 0) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dailySummaries.map((summary) => (
            <DailySessionCard dailySummary={summary} key={summary.date} />
          ))}
        </div>
      );
    }

    return (
      <Card className="bg-[#0F172A] border-[#334155]" padding="lg" variant="default">
        <Stack align="center" direction="col" gap={4}>
          <div className="flex items-center justify-center w-16 h-16 rounded-tracq bg-gradient-to-br from-tracq-deep-blue/20 to-tracq-bright-blue/20 border border-tracq-bright-blue/30">
            <Calendar className="w-8 h-8 text-tracq-bright-blue" />
          </div>
          <Stack align="center" direction="col" gap={2}>
            <Heading className="text-[#F1F5F9] font-bold" level="h4" size="lg">
              No Session History
            </Heading>
            <p className="text-[#94A3B8] text-center max-w-md">
              You haven&apos;t completed any sessions yet. Start your first session to see your
              progress here.
            </p>
          </Stack>
          <Button
            className="bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90 text-white font-semibold"
            onClick={handleNavigateToDashboard}
            variant="primary"
          >
            Start Your First Session
          </Button>
        </Stack>
      </Card>
    );
  };

  return (
    <div className="space-y-8 p-6">
      {/* Header */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack align="center" direction="row" justify="between">
          <Stack align="center" direction="row" gap={4}>
            <Button
              className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
              onClick={handleNavigateToDashboard}
              size="sm"
              variant="outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <Stack align="center" direction="row" gap={3}>
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
                <History className="w-6 h-6 text-white" />
              </div>
              <Stack direction="col" gap={1}>
                <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
                  Session History
                </Heading>
                <p className="text-[#94A3B8]">Your daily sessions and responses</p>
              </Stack>
            </Stack>
          </Stack>
          <Button
            className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
            disabled={summariesLoading}
            onClick={handleRefresh}
            variant="outline"
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", summariesLoading && "animate-spin")} />
            {summariesLoading ? "Loading..." : "Refresh"}
          </Button>
        </Stack>
      </Card>

      {/* Error Display */}
      {summariesError ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <Stack className="flex-1" direction="col" gap={1}>
              <p className="text-red-300 text-sm font-medium">Failed to load session history</p>
              <p className="text-red-400 text-xs">
                {summariesError instanceof Error
                  ? summariesError.message
                  : "Unknown error occurred"}
              </p>
            </Stack>
            <Button
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              onClick={handleRefresh}
              size="sm"
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </Stack>
        </Card>
      ) : null}

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          className="bg-[#1E293B] border-[#334155] hover:border-tracq-bright-blue/50 transition-all duration-200 hover:shadow-tracq-card"
          padding="md"
          variant="default"
        >
          <Stack align="center" direction="row" gap={3}>
            <div className="flex items-center justify-center w-10 h-10 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <Stack direction="col" gap={1}>
              <p className="text-[#94A3B8] text-sm">Total Days</p>
              <p className="text-[#F1F5F9] text-2xl font-bold">
                {summariesLoading ? "..." : (dailySummaries?.length ?? 0)}
              </p>
            </Stack>
          </Stack>
        </Card>

        <Card
          className="bg-[#1E293B] border-[#334155] hover:border-tracq-bright-blue/50 transition-all duration-200 hover:shadow-tracq-card"
          padding="md"
          variant="default"
        >
          <Stack align="center" direction="row" gap={3}>
            <div className="flex items-center justify-center w-10 h-10 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <Stack direction="col" gap={1}>
              <p className="text-[#94A3B8] text-sm">Completion Rate</p>
              <p className="text-[#F1F5F9] text-2xl font-bold">
                {summariesLoading ? "..." : calculateCompletionRate(dailySummaries)}%
              </p>
            </Stack>
          </Stack>
        </Card>

        <Card
          className="bg-[#1E293B] border-[#334155] hover:border-tracq-bright-blue/50 transition-all duration-200 hover:shadow-tracq-card"
          padding="md"
          variant="default"
        >
          <Stack align="center" direction="row" gap={3}>
            <div className="flex items-center justify-center w-10 h-10 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <Stack direction="col" gap={1}>
              <p className="text-[#94A3B8] text-sm">Total Sessions</p>
              <p className="text-[#F1F5F9] text-2xl font-bold">
                {summariesLoading ? "..." : calculateTotalSessions(dailySummaries)}
              </p>
            </Stack>
          </Stack>
        </Card>
      </div>

      {/* Session History Cards */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack direction="col" gap={6}>
          <Stack align="center" direction="row" justify="between">
            <Heading className="text-[#F1F5F9] font-bold" level="h3" size="xl">
              Daily Overview
            </Heading>
            <Badge
              className="bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30"
              size="sm"
              variant="info"
            >
              {summariesLoading ? "Loading..." : `${String(dailySummaries?.length ?? 0)} days`}
            </Badge>
          </Stack>

          {getSessionHistoryContent()}
        </Stack>
      </Card>
    </div>
  );
};
